import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ReactNode } from 'react';

import { Color } from '@/constants/color';
import { ValkeyUnavailableResponse } from '@/types/engDashboard';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Spinner from '@/ui/components/Spinner';

import { POLL_OPTIONS, PollInterval } from './types';

const STATUS_COLOR: Record<string, Color> = {
  SUCCESS: Color.GREEN,
  FAILURE: Color.RED,
  STARTED: Color.BLUE,
  RETRY: Color.YELLOW,
  REVOKED: Color.GRAY
};

const statusColor = (status: string | null): Color =>
  (status && STATUS_COLOR[status]) || Color.GRAY;

export const Loading = () => (
  <div className="py-4">
    <Spinner />
  </div>
);

export const PanelCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card className="mb-4">
    <h2 className="text-lg font-semibold mb-2">{title}</h2>
    {children}
  </Card>
);

const describeError = (error: unknown) => {
  if (!isAxiosError<Partial<ValkeyUnavailableResponse>>(error)) {
    return { heading: 'Request failed', detail: String(error) };
  }
  const { response } = error;
  if (!response) {
    return { heading: 'No response from the backend', detail: error.message };
  }
  const connection = response.data?.connection;
  return {
    heading: connection ? `HTTP ${response.status} · ${connection}` : `HTTP ${response.status}`,
    detail: response.data?.detail ?? response.statusText
  };
};

export const isNotFound = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

export const ErrorDetail = ({ error }: { error: unknown }) => {
  const { heading, detail } = describeError(error);
  return (
    <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <div className="font-semibold">{heading}</div>
      <div className="font-mono break-all">{detail}</div>
    </div>
  );
};

export const PanelError = ({ title, error }: { title: string; error: unknown }) => (
  <PanelCard title={title}>
    <ErrorDetail error={error} />
  </PanelCard>
);

export const StatusBadge = ({ status }: { status: string | null }) => (
  <Badge color={statusColor(status)}>{status ?? 'unknown'}</Badge>
);

// With auto-refresh off by default, this is how you get fresh numbers.
// `type: 'active'` refetches exactly the mounted panel's queries, so there is
// no key list here to fall out of date as panels come and go.
export const RefreshButton = () => {
  const queryClient = useQueryClient();
  return (
    <Button color={Color.GRAY} onClick={() => queryClient.refetchQueries({ type: 'active' })}>
      <FontAwesomeIcon icon={faRotateRight} className="mr-1" />
      Refresh
    </Button>
  );
};

export const PollingControl = ({
  value,
  onChange
}: {
  value: PollInterval;
  onChange: (value: PollInterval) => void;
}) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-gray-500">Auto-refresh</span>
    {POLL_OPTIONS.map((option) => (
      <Button
        key={option.label}
        color={option.value === value ? Color.BLUE : Color.GRAY}
        onClick={() => onChange(option.value)}>
        {option.label}
      </Button>
    ))}
  </div>
);
